import React from 'react';
import styled from 'styled-components';
import { Button, Col, Empty, Progress, Row, Spin } from 'antd';
import {
  CheckCircleFilled,
  ReloadOutlined,
  RightOutlined,
  UserOutlined,
  WarningFilled
} from '@ant-design/icons';
import { pressable } from 'src/constants/checklists';
import { runSubtitle, summariseHistory } from 'src/constants/checklistRuns';
import { ANYONE, workByPerson } from 'src/constants/checklistTeam';

const GREEN = '#2f8a4c';
const RED = '#c8442b';
const AMBER = '#b9770e';
const MUTED = '#5b6660';

// Outstanding items shown per person before "See all".
const PREVIEW = 4;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 1.1em;
  .chip {
    border-radius: 999px;
    padding: 0.25em 0.8em;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .spacer {
    flex: 1;
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 0.75em 1em;
  margin-bottom: 0.5em;
  border-radius: 12px;
  background: #fdecea;
  color: #7d2617;
  cursor: pointer;
  .anticon-warning {
    color: ${RED};
    font-size: 20px;
  }
  .text {
    flex: 1;
    min-width: 0;
  }
  .where {
    font-size: 12.5px;
    opacity: 0.85;
  }
  &:focus-visible {
    outline: 2px solid ${RED};
    outline-offset: 2px;
  }
`;

const Person = styled.div`
  background: #fff;
  border: 1px solid #e3ebe5;
  border-radius: 14px;
  padding: 0.9em 1em;
  height: 100%;
  box-shadow: inset 4px 0 0 ${p => p.$stripe};

  .head {
    display: flex;
    align-items: center;
    gap: 0.6em;
  }
  .name {
    flex: 1;
    min-width: 0;
    font-weight: 800;
    font-size: 16px;
  }
  .sub {
    color: ${MUTED};
    font-size: 12.5px;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35em;
    margin: 0.4em 0 0.2em;
  }
  .chip {
    border-radius: 999px;
    padding: 0.05em 0.6em;
    font-size: 12px;
    font-weight: 700;
  }
`;

const Todo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  min-height: 44px;
  padding: 0.35em 0.2em;
  border-top: 1px solid #eef2ef;
  cursor: pointer;
  font-size: 13.5px;
  &:hover {
    background: #f7faf8;
  }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: -2px;
  }
  .what {
    flex: 1;
    min-width: 0;
  }
  .what div:first-child {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .when {
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
`;

const stateColor = key =>
  key === 'overdue' ? RED : key === 'dueSoon' ? AMBER : MUTED;

const TeamView = ({ runs = [], loading, now, onOpen, onShowPerson, onRefresh }) => {
  const current = now();
  const people = workByPerson(runs, current);
  const critical = summariseHistory(runs, current).issues.filter(i => i.critical);
  const byId = runs.reduce((acc, r) => ({ ...acc, [r._id]: r }), {});

  const behind = people.filter(p => p.overdue > 0 && p.person.id !== ANYONE.id).length;
  const openParts = people.reduce((n, p) => n + p.outstanding.length, 0);

  return (
    <Spin spinning={Boolean(loading)}>
      <Bar>
        {behind > 0 && (
          <span className="chip" style={{ color: RED, background: '#fdecea' }}>
            {behind} {behind === 1 ? 'person' : 'people'} behind
          </span>
        )}
        <span className="chip" style={{ color: MUTED, background: '#f1f4f2' }}>
          {openParts} section{openParts === 1 ? '' : 's'} outstanding
        </span>
        {critical.length > 0 && (
          <span className="chip" style={{ color: '#fff', background: RED }}>
            {critical.length} critical
          </span>
        )}
        <span className="spacer" />
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          Refresh
        </Button>
      </Bar>

      {critical.map(issue => (
        <Alert key={issue.key} {...pressable(() => byId[issue.runId] && onOpen(byId[issue.runId]))}>
          <WarningFilled />
          <span className="text">
            <strong>
              {issue.itemLabel}
              {issue.type === 'reading' && issue.value !== undefined ? ` — ${issue.value}${issue.unit}` : ''}
            </strong>
            <div className="where">
              {issue.templateName} · {issue.sectionTitle}
              {issue.byName ? ` · ${issue.byName}` : ''}
              {issue.note ? ` · "${issue.note}"` : ''}
            </div>
          </span>
          <RightOutlined />
        </Alert>
      ))}

      {!people.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No checklists today."
          style={{ padding: '3em 0' }}
        />
      ) : (
        <Row gutter={[12, 12]} style={{ marginTop: critical.length ? '0.8em' : 0 }}>
          {people.map(p => {
            const stripe = p.overdue ? RED : p.outstanding.length ? AMBER : GREEN;
            return (
              <Col xs={24} md={12} xl={8} key={p.person.id}>
                <Person $stripe={stripe}>
                  <div className="head">
                    <UserOutlined style={{ color: MUTED }} />
                    <span className="name">{p.person.name}</span>
                    <span className="sub">
                      {p.checklists} checklist{p.checklists === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Progress
                    percent={p.percent}
                    size="small"
                    strokeColor={p.outstanding.length ? (p.overdue ? RED : AMBER) : GREEN}
                    format={() => `${p.answered}/${p.items}`}
                    style={{ margin: '0.35em 0 0' }}
                  />
                  {(p.overdue > 0 || p.issues > 0) && (
                    <div className="chips">
                      {p.overdue > 0 && (
                        <span className="chip" style={{ color: RED, background: '#fdecea' }}>
                          {p.overdue} overdue
                        </span>
                      )}
                      {p.critical > 0 && (
                        <span className="chip" style={{ color: '#fff', background: RED }}>
                          {p.critical} critical
                        </span>
                      )}
                      {p.issues - p.critical > 0 && (
                        <span className="chip" style={{ color: AMBER, background: '#fdf3e2' }}>
                          {p.issues - p.critical} issue{p.issues - p.critical === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  )}

                  {p.outstanding.length ? (
                    <div style={{ marginTop: '0.4em' }}>
                      {p.outstanding.slice(0, PREVIEW).map(o => (
                        <Todo key={`${o.run._id}-${o.section.key}`} {...pressable(() => onOpen(o.run))}>
                          <span className="what">
                            <div>
                              {o.run.templateName}
                              {runSubtitle(o.run) ? ` · ${runSubtitle(o.run)}` : ''}
                            </div>
                            <div className="sub">
                              {o.section.title} · {o.remaining} left
                            </div>
                          </span>
                          <span className="when" style={{ color: stateColor(o.state.key) }}>
                            {o.state.label}
                          </span>
                          <RightOutlined style={{ color: MUTED }} />
                        </Todo>
                      ))}
                      {p.outstanding.length > PREVIEW && onShowPerson && (
                        <Button type="link" onClick={() => onShowPerson(p.person.id)} style={{ padding: 0 }}>
                          See all {p.outstanding.length} on Today
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: GREEN, fontWeight: 600, marginTop: '0.5em' }}>
                      <CheckCircleFilled /> All done
                    </div>
                  )}
                </Person>
              </Col>
            );
          })}
        </Row>
      )}
    </Spin>
  );
};

export default TeamView;
